import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchApiData } from "../../redux/reducers/commonApiSlice";
import {
  formatDateTimezon,
  formatTimezoneThenStraight,
} from "../common-data/Converts";
import { formattedLongDate2 } from "../common-data/Converts";

const FitnessProfileCharts = ({
  rowData,
  userInfoData,
  onClose,
  isBCALogs,
}) => {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector((state) => state.common);
  const [allMemberFitnessInfo, setAllMemberFitnessInfo] = useState([]);
  const [page, setPage] = useState(1);
  const limit = 30;
  const [paginationInfo, setPaginationInfo] = useState({ total: 0, pages: 1 });

  const [selectedIdx, setSelectedIdx] = useState(0);

  function getMemberInfo(filters) {
    const endpoint = isBCALogs
      ? "/member/fitness_profiles/bca_logs"
      : "/member/fitness_profiles_logs";

    dispatch(
      fetchApiData({
        method: "GET",
        endpoint: endpoint,
        payload: {
          filters: filters,
        },
        token: userInfoData?.token,
      })
    );
  }

  const getDaysLeft = (start, end) => {
    if (!start || !end) return 0;
    const now = new Date();
    const expire = new Date(end);
    const diff = Math.ceil((expire - now) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  const getTotalDays = (start, end) => {
    if (!start || !end) return 0;
    const s = new Date(start);
    const e = new Date(end);
    return Math.ceil((e - s) / (1000 * 60 * 60 * 24));
  };

  const sortedLogs = [...allMemberFitnessInfo].sort(
    (a, b) => b.date_added - a.date_added
  );
  const selectedLog = sortedLogs[selectedIdx] || {};
  const daysLeft = getDaysLeft(selectedLog.start_date, selectedLog.expire_date);
  const totalDays = getTotalDays(
    selectedLog.start_date,
    selectedLog.expire_date
  );
  const daysPercent = totalDays
    ? Math.round(((totalDays - daysLeft) / totalDays) * 100)
    : 0;

  const isBCA = !!isBCALogs;
  const logsToShow = isBCA ? allMemberFitnessInfo : sortedLogs;
  const selectedBcaLog = isBCA
    ? allMemberFitnessInfo[selectedIdx] || {}
    : selectedLog;

  useEffect(() => {
    if (rowData?.user_id) {
      const sendableFilters = {
        user_id: rowData.user_id,
        page: page,
        limit: limit,
      };
      getMemberInfo(sendableFilters, page, limit);
    }
  }, [rowData?.user_id, page]);

  useEffect(() => {
    if (!loading && !error && data?.status && data?.data) {
      setAllMemberFitnessInfo(data.data);
      if (data.pagination) {
        const paginationData = data.pagination[0] || {};

        setPaginationInfo({
          total: Number(paginationData.pagination) || 0,
          pages: Number(paginationData.pages) || 1,
        });
      } else {
        setPaginationInfo({ total: 0, pages: 1 });
      }
    } else {
      setAllMemberFitnessInfo([]);
      setPaginationInfo({ total: 0, pages: 1 });
    }
  }, [data, loading, error]);

  return (
    <div className="fit-logs-container">
      <div className="fit-logs-member-info">
        <div>
          <h2>{rowData?.name}</h2>

          <div className="fit-logs-member-contact">
            <span>📞 {rowData?.mobile}</span> <span>✉️ {rowData?.email}</span>
          </div>

          <div className="fit-logs-member-trainer">
            <b>Trainer:</b> {rowData?.trainer_name}
          </div>

          <div className="fit-logs-member-gym">{rowData?.gym_name}</div>

          <div className="fit-logs-member-meta">
            <span>
              <b>Gender:</b>{" "}
              {rowData?.gender
                ? rowData.gender.charAt(0).toUpperCase() +
                  rowData.gender.slice(1)
                : "-"}
            </span>
            <span style={{ marginLeft: 16 }}>
              <b>Age:</b> {rowData?.age || "-"}
            </span>
          </div>

          <div className="fit-logs-member-plan-statuses">
            <div>
              <span role="img" aria-label="pt">
                🏋️
              </span>{" "}
              <b>PT Plan Status:</b>{" "}
              <span
                className={`fit-logs-status-badge ${rowData?.pt_plan_status?.toLowerCase()}`}
              >
                {rowData?.pt_plan_status?.replace(/_/g, " ")}
              </span>
            </div>
            <div>
              <span role="img" aria-label="plan">
                📅
              </span>{" "}
              <b>Regular Plan Status:</b>{" "}
              <span
                className={`fit-logs-status-badge ${rowData?.regular_plan_status?.toLowerCase()}`}
              >
                {rowData?.regular_plan_status}
              </span>
            </div>
            {rowData?.start_date && rowData?.expire_date && (
              <>
                <div className="fit-logs-plan-duration">
                  <b>Regular Plan Duration:</b>{" "}
                  {formatDateTimezon(rowData.start_date)} -{" "}
                  {formatDateTimezon(rowData.expire_date)}
                </div>
                <div className="fit-logs-plan-days-row">
                  <span className="fit-logs-plan-days-label">
                    <b>Days Left (Regular Plan):</b>{" "}
                    {getDaysLeft(rowData.start_date, rowData.expire_date)} Days
                  </span>
                  <div className="fit-logs-plan-days-bar fit-logs-progress-bar-bg enhanced">
                    <div
                      className="fit-logs-progress-bar-fg enhanced"
                      style={{
                        width: `${Math.max(
                          0,
                          Math.min(
                            100,
                            Math.round(
                              (getDaysLeft(
                                rowData.start_date,
                                rowData.expire_date
                              ) /
                                getTotalDays(
                                  rowData.start_date,
                                  rowData.expire_date
                                )) *
                                100
                            )
                          )
                        )}%`,
                        height: "100%",
                      }}
                    >
                      <span className="fit-logs-plan-days-bar-text">
                        {Math.max(
                          0,
                          Math.min(
                            100,
                            Math.round(
                              (getDaysLeft(
                                rowData.start_date,
                                rowData.expire_date
                              ) /
                                getTotalDays(
                                  rowData.start_date,
                                  rowData.expire_date
                                )) *
                                100
                            )
                          )
                        )}
                        %
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <button
          className="fit-logs-close-btn"
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>
      </div>

      <div className="fit-logs-main">
        {/* Left: Dates */}
        <div
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <div
            className="fit-logs-dates-list enhanced"
            style={{ flex: 1, minHeight: 0 }}
          >
            {logsToShow.slice(0, limit).map((log, idx) => {
              let status = isBCA
                ? log.status?.toUpperCase()
                : log.fitness_status?.toUpperCase();
              let statusClass = "";

              if (status) {
                if (status === "UPCOMING")
                  statusClass = "fit-logs-status-upcoming";
                else if (status === "COMPLETED")
                  statusClass = "fit-logs-status-completed";
                else if (status === "MISSED")
                  statusClass = "fit-logs-status-missed";
              }

              const mainDate = isBCA
                ? formatDateTimezon(log?.date_added)
                : formattedLongDate2(log?.date_added);
              const scheduledDate = isBCA
                ? formatTimezoneThenStraight(log?.scheduled_date)
                : log?.next_scheduled_date;
              // const mainDate = isBCA
              // ? formatTimezoneThenStraight(log.date_added)
              // : formattedLongDate2(log.date_added);
              // const scheduledDate = isBCA
              //   ? formatDateTimezon(log.scheduled_date)
              //   : log.next_scheduled_date;

              return (
                <div
                  key={log.date_added || log.scheduled_date}
                  className={`fit-logs-date-item enhanced ${
                    idx === selectedIdx ? "active" : ""
                  }`}
                  onClick={() => setSelectedIdx(idx)}
                >
                  <div className="fit-logs-date-content-vertical">
                    <div
                      className={`fit-logs-status-label-no-bg ${statusClass}`}
                    >
                      {status}
                    </div>

                    <div className="fit-logs-date-main">{scheduledDate}</div>

                    <div className="fit-logs-date-scheduled">
                      Added On: {mainDate || "-"}
                    </div>
                    {/* <div className="fit-logs-date-scheduled">
                      Scheduled On: {mainDate || "-"}
                    </div> */}
                  </div>
                </div>
              );
            })}
          </div>

          {paginationInfo.pages > 1 && (
            <div className="fit-logs-pagination-arrows">
              <button
                className="fit-logs-pagination-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                aria-label="Previous Page"
              >
                &#8592;
              </button>
              <span style={{ fontSize: 14, color: "#888", margin: "0 6px" }}>
                Page {page} of {paginationInfo.pages}
              </span>
              <button
                className="fit-logs-pagination-btn"
                onClick={() =>
                  setPage((p) =>
                    !allMemberFitnessInfo || allMemberFitnessInfo.length < limit
                      ? p
                      : p + 1
                  )
                }
                disabled={page >= paginationInfo.pages}
                aria-label="Next Page"
              >
                &#8594;
              </button>
            </div>
          )}

          <div className="fit-logs-pagination-total">
            Total: {paginationInfo.total} logs
          </div>
        </div>

        {/* Right: Log Details */}
        <div className="fit-logs-log-details enhanced">
          <ul className="fit-logs-log-metrics">
            {isBCA ? (
              <h3>
                BCA Log{" "}
                <span className="fit-logs-log-date">
                  {formatTimezoneThenStraight(selectedBcaLog.scheduled_date)}
                </span>
              </h3>
            ) : (
              <h3>
                Fitness Log{" "}
                <span className="fit-logs-log-date">
                  {formattedLongDate2(selectedLog.date_added)}
                </span>
              </h3>
            )}

            {isBCA ? (
              <>
                <li>
                  <button
                    type="button"
                    className="fit-logs-bca-download-btn"
                    disabled={!selectedBcaLog?.bca_report}
                    onClick={() =>
                      window.open(selectedBcaLog?.bca_report, "_blank")
                    }
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      opacity: selectedBcaLog?.bca_report ? 1 : 0.5,
                      cursor: selectedBcaLog?.bca_report
                        ? "pointer"
                        : "not-allowed",
                    }}
                  >
                    <span role="img" aria-label="download">
                      ⬇️
                    </span>{" "}
                    Download BCA Report
                  </button>
                </li>

                <li>
                  <span role="img" aria-label="trainer">
                    🏋️
                  </span>{" "}
                  <b>Trainer:</b> {selectedBcaLog.trainer_name}
                </li>

                <li>
                  <span role="img" aria-label="calendar">
                    📅
                  </span>{" "}
                  <b>Scheduled Date:</b>{" "}
                  {formatDateTimezon(selectedBcaLog?.scheduled_date)}
                </li>

                <li>
                  <span role="img" aria-label="calendar">
                    📅
                  </span>{" "}
                  <b>Completed On:</b>{" "}
                  {selectedBcaLog.completed_on
                    ? formatDateTimezon(selectedBcaLog.completed_on)
                    : "-"}
                </li>

                <li>
                  <span role="img" aria-label="status">
                    ⏳
                  </span>{" "}
                  <b>BCA Status:</b>{" "}
                  <span
                    className={`fit-logs-status-badge ${selectedBcaLog.status?.toLowerCase()}`}
                  >
                    {selectedBcaLog.status}
                  </span>
                </li>

                <li>
                  <span role="img" aria-label="calendar">
                    📅
                  </span>{" "}
                  <b>Next Scheduled Date:</b>{" "}
                  {selectedBcaLog.next_scheduled_date
                    ? formatDateTimezon(selectedBcaLog.next_scheduled_date)
                    : "-"}
                </li>

                <li>
                  <span role="img" aria-label="trainer">
                    🏋️
                  </span>{" "}
                  <b>Next Scheduled Trainer:</b>{" "}
                  {selectedBcaLog?.scheduled_trainer_name}
                </li>
              </>
            ) : (
              <>
                <li>
                  <span role="img" aria-label="weight">
                    ⚖️
                  </span>{" "}
                  <b>Weight:</b>{" "}
                  {parseFloat(selectedLog?.current_weight || 0).toFixed(2)} kg
                </li>

                <li>
                  <span role="img" aria-label="target">
                    🎯
                  </span>{" "}
                  <b>Target Weight:</b>{" "}
                  {parseFloat(selectedLog?.target_weight || 0).toFixed(2)} kg
                </li>

                <li>
                  <span role="img" aria-label="fire">
                    🔥
                  </span>{" "}
                  <b>Calorie:</b>{" "}
                  {parseFloat(selectedLog?.current_calorie_result || 0).toFixed(
                    2
                  )}
                </li>

                <li>
                  <span role="img" aria-label="target">
                    🥗
                  </span>{" "}
                  <b>Target Calorie:</b>{" "}
                  {parseFloat(selectedLog?.calorie_target_result || 0).toFixed(
                    2
                  )}
                </li>

                <li>
                  <span role="img" aria-label="bmi">
                    📏
                  </span>{" "}
                  <b>BMI:</b>{" "}
                  {parseFloat(selectedLog?.bmi_result || 0).toFixed(2)}
                </li>

                <li>
                  <span role="img" aria-label="tdee">
                    ⚡
                  </span>{" "}
                  <b>TDEE:</b> {parseFloat(selectedLog?.tdee || 0).toFixed(2)}
                </li>

                <li>
                  <span role="img" aria-label="fat">
                    🧈
                  </span>{" "}
                  <b>FAT:</b>{" "}
                  {parseFloat(selectedLog?.fat_result || 0).toFixed(2)}
                </li>

                <li>
                  <span role="img" aria-label="how active">
                    🏃‍♂️
                  </span>{" "}
                  <b>how active:</b>{" "}
                  {selectedLog?.how_active?.replace(/_/g, " ") || "-"}
                </li>

                <li>
                  <span role="img" aria-label="goal">
                    🥅
                  </span>{" "}
                  <b>goal:</b> {selectedLog?.goal?.replace(/_/g, " ") || "-"}
                </li>

                <li>
                  <span role="img" aria-label="body type">
                    🧍
                  </span>{" "}
                  <b>body type:</b>{" "}
                  {selectedLog?.body_type?.replace(/_/g, " ") || "-"}
                </li>

                <li>
                  <span role="img" aria-label="result value">
                    📊
                  </span>{" "}
                  <b>fitness result:</b>{" "}
                  {selectedLog?.result_value?.replace(/_/g, " ") || "-"}
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FitnessProfileCharts;
